import React, { useState, useEffect } from 'react'
import TextInput from 'components/ui/forms/field/text-input/TextInput'
import { Container, Col, Row, ButtonToolbar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import * as fromActions from 'store/actions/blocks.action'
import { IMainState } from 'store/states'
import styles from './Task.module.scss'
import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import Button from 'design-system/button'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import TextAreaInput from 'components/ui/forms/field/text-area/TextArea'
import SwitchCustom from 'components/ui/forms/field/switch-custom/SwitchCustom'
import CheckBoxRadioButtonInput from 'components/ui/forms/field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'

interface ITask {
  onCancel: any
  editing: boolean
}

const defaultValues = {
  name: '',
  description: '',
  multiFile: true,
  mandatory: false,
  allowPublic: false,
  containFiles: false,
  hasResponses: false,
}

const Task: React.FC<ITask> = ({ onCancel, editing }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [data, setData] = useState(defaultValues)
  const { edit } = useSelector(({ blocks }: IMainState) => blocks)

  useEffect(() => {
    setData({ ...defaultValues, ...edit })
  }, [edit])

  const handleOnSave = () => {
    const { id, lessonId } = edit
    dispatch({ type: fromActions.EDIT_TASK_BLOCK, payload: { ...data, id, lessonId } })
    onCancel()
  }

  const disabledMessage = t('administration.blocks.type.task.messages.disabledOptionMsg')
  const tooltip = t('administration.blocks.type.task.messages.tooltipAllowPublicMsg')
  const options = [t('administration.blocks.type.task.multiFiles.yes'), t('administration.blocks.type.task.multiFiles.no')]
  return (
    <Container fluid>
      <Row>
        <h1 className={styles.title}>{t(`administration.blocks.type.task.${editing ? 'edit' : 'new'}`)}</h1>
      </Row>
      <Row>
        <Col xs={6} className={'p-0 pt-3'}>
          <TextInput
            name={'name'}
            value={data['name']}
            label={t('administration.blocks.type.task.title')}
            placeHolder={''}
            onChange={(e: any) => setData({ ...data, ['name']: e.target.value })}
            error={''}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className={'p-0 pt-3'}>
          <TextAreaInput
            name={'description'}
            rows={3}
            label={t('administration.blocks.type.task.description')}
            value={data['description']}
            onChange={(e: any) => setData({ ...data, ['description']: e.target.value })}
            error={''}
          />
        </Col>
      </Row>
      <Row>
        <CommonToolTip text={t('administration.blocks.hasResponses')} disabled={!data['containFiles']} position="top">
          <div className="d-block">
            <SwitchCustom
              name={'multiFile'}
              value={data['multiFile']}
              label={t('administration.blocks.type.task.multiFiles.title')}
              onChange={(value: boolean) => setData({ ...data, ['multiFile']: value })}
              options={options}
              disabled={data['containFiles']}
            />
          </div>
        </CommonToolTip>
      </Row>
      <Row>
        <CommonToolTip text={t('administration.blocks.hasResponses')} disabled={!data['containFiles']} position="top">
          <div className="d-block">
            <SwitchCustom
              name={'allowPublicFile'}
              value={data['allowPublic']}
              label={t('administration.blocks.type.task.allowPublic.title')}
              onChange={(value: boolean) => setData({ ...data, ['allowPublic']: value })}
              options={options}
              disabled={data['containFiles']}
              disabledMessage={disabledMessage}
              tooltipMessage={tooltip}
              className={styles.tooltip}
            />
          </div>
        </CommonToolTip>
      </Row>
      <Row>
        <CommonToolTip text={t('administration.blocks.hasResponses')} disabled={!data['containFiles']} position="top">
          <div>
            <InputLabel label={t('administration.blocks.type.task.taskRequired.title')} />
            <CheckBoxRadioButtonInput
              name={'mandatory-task-true'}
              value={true}
              checked={data['mandatory']}
              inline={true}
              type={'radio'}
              label={t('administration.blocks.type.task.taskRequired.yes')}
              onChange={() => setData({ ...data, ['mandatory']: true })}
              error={''}
              disabled={data['containFiles']}
            />
            <CheckBoxRadioButtonInput
              name={'mandatory-task-false'}
              value={false}
              checked={!data['mandatory']}
              inline={true}
              type={'radio'}
              label={t('administration.blocks.type.task.taskRequired.no')}
              onChange={() => setData({ ...data, ['mandatory']: false })}
              error={''}
              disabled={data['containFiles']}
            />
          </div>
        </CommonToolTip>
      </Row>
      <ButtonToolbar className="float-right mt-5">
        <Button onClick={onCancel} variant={ButtonVariant.Secondary} size={ButtonSizes.Small}>
          {t('administration.blocks.type.task.buttons.cancel')}
        </Button>
        <Button size={ButtonSizes.Small} onClick={handleOnSave}>
          {t('administration.blocks.type.task.buttons.accept')}
        </Button>
      </ButtonToolbar>
    </Container>
  )
}

export default Task
